import { differenceInDays, parse, subMonths } from 'date-fns';
import {
  getLastLatePayment,
  getLatePayments,
  getOnTimePayments,
  isCollection,
  isOpenAccount,
} from './credit-report-utils';
import type { CreditReportType, ExperianDescription, FullCreditProfile, LocalDate } from '@zg-rentals/ts-capps-client';
import { DATA_UNAVAILABLE_TEXT } from '../../constants/credit-report-constants';
import { getCurrencyDisplayFromDollars, getTitleCase, parseDateObject } from '@zg-rentals/formatters';
import type { PaymentAccount } from '../../models';

export const getCreditAccounts = (creditReport: CreditReportType) => {
  const { fullCreditProfile } = creditReport;
  const openAccounts: Array<PaymentAccount> = [];
  const closedAccounts: Array<PaymentAccount> = [];
  if (fullCreditProfile && fullCreditProfile.TradeLine) {
    const { TradeLine } = fullCreditProfile;
    const allAccounts = TradeLine.filter((account) => !isCollection(account));
    allAccounts.forEach((account) => {
      const {
        OpenDate,
        SubscriberDisplayName,
        BalanceAmount,
        AccountType,
        BalanceDate,
        PaymentProfile,
        ConsumerComment,
        SpecialComment,
      } = account;
      const onTimePayments = PaymentProfile ? getOnTimePayments(PaymentProfile) : DATA_UNAVAILABLE_TEXT;
      const latePayments = PaymentProfile ? getLatePayments(PaymentProfile) : DATA_UNAVAILABLE_TEXT;
      const lastLatePayment =
        PaymentProfile && BalanceDate ? getLastLatePayment(PaymentProfile, BalanceDate) : DATA_UNAVAILABLE_TEXT;
      const accountTypeAndName = getAccountTypeAndName(AccountType?.desc, SubscriberDisplayName);
      const hasAccountName = AccountType?.desc || SubscriberDisplayName;
      const isOpen = isOpenAccount(account);
      if (BalanceAmount || hasAccountName) {
        const newAccount: PaymentAccount = {
          name: accountTypeAndName,
          amount: BalanceAmount ? getCurrencyDisplayFromDollars({ amount: BalanceAmount, hideCents: true }) : '$0',
          openDate: OpenDate || DATA_UNAVAILABLE_TEXT,
          onTimePayments: onTimePayments !== null ? `${onTimePayments}` : DATA_UNAVAILABLE_TEXT,
          latePayments: latePayments !== null ? `${latePayments}` : DATA_UNAVAILABLE_TEXT,
          lastLatePayment: lastLatePayment !== null ? lastLatePayment : DATA_UNAVAILABLE_TEXT,
          accountComment: getAccountComment(ConsumerComment, SpecialComment),
          paymentHistory: getPaymentHistory(BalanceDate, PaymentProfile),
        };
        if (isOpen) {
          openAccounts.push(newAccount);
        } else {
          closedAccounts.push(newAccount);
        }
      }
    });
  }
  return { openAccounts, closedAccounts };
};

export const getAccountTypeAndName = (description?: string, name?: string) => {
  if (!description && !name) {
    return 'name unavailable';
  }
  if (!name) {
    return description || '';
  }
  return `${getTitleCase(name)}${description ? ` ${description}` : ''}`;
};

const getAccountComment = (consumerComment?: string, specialComment?: ExperianDescription) => {
  if (consumerComment) {
    return consumerComment;
  }
  if (specialComment?.desc) {
    return specialComment.desc;
  }
  return null;
};

const getPaymentHistory = (balanceDateString?: string | LocalDate, paymentProfileString?: string) => {
  if (!balanceDateString || !paymentProfileString) {
    return [];
  }
  const balanceDate =
    typeof balanceDateString === 'string'
      ? parse(balanceDateString, 'MM/dd/yyyy', new Date())
      : parseDateObject(balanceDateString);
  const paymentHistory = [];
  let prevYear = balanceDate.getFullYear();
  let values = [];
  for (let i = 0; i < paymentProfileString.length + 1; i++) {
    const curDate = subMonths(balanceDate, i);
    if (curDate.getFullYear() !== prevYear || i === paymentProfileString.length) {
      paymentHistory.push({
        year: prevYear,
        values,
      });
      values = [];
      prevYear = curDate.getFullYear();
    }
    values.push({
      month: curDate.getMonth() + 1,
      value: paymentProfileString[i],
    });
  }
  return paymentHistory;
};

export const getAverageAgeOpenAccounts = (creditReport: {
  fullCreditProfile?: Pick<FullCreditProfile, 'TradeLine'>;
}) => {
  const { fullCreditProfile } = creditReport;
  if (fullCreditProfile && fullCreditProfile.TradeLine) {
    const { TradeLine } = fullCreditProfile,
      allAccounts = TradeLine.filter((account) => !isCollection(account) && isOpenAccount(account));
    let totalAge = 0;
    let totalAccountsWithAge = 0;
    allAccounts.forEach((account) => {
      const { OpenDate } = account;
      if (OpenDate) {
        const accountAge = getAccountAge(OpenDate, new Date());
        totalAge += accountAge;
        totalAccountsWithAge += 1;
      }
    });
    if (totalAccountsWithAge === 0) {
      return 'Unavailable';
    }
    return daysInWords(totalAge / totalAccountsWithAge);
  } else {
    return null;
  }
};

export const getOldestAccountAge = (creditReport: CreditReportType) => {
  const { fullCreditProfile } = creditReport;
  if (fullCreditProfile && fullCreditProfile.TradeLine) {
    const { TradeLine } = fullCreditProfile,
      allAccounts = TradeLine.filter((account) => !isCollection(account));
    let oldestAge: number | null = null;
    allAccounts.forEach((account) => {
      const { OpenDate } = account;
      if (OpenDate) {
        const accountAge = getAccountAge(OpenDate, new Date());
        if (!oldestAge || accountAge > oldestAge) {
          oldestAge = accountAge;
        }
      }
    });
    return oldestAge !== null ? daysInWords(oldestAge) : 'Unavailable';
  } else {
    return 'Unavailable';
  }
};

const getAccountAge = (pastDate: string | Date | LocalDate, currentDate: string | Date) => {
  // -0 exists in javascript and is equal to 0, but unit test comparison gets confused.
  // adding 0 at the end makes sure we don't have -0
  return differenceInDays(currentDate, parseDateObject(pastDate)) + 0;
};

const daysInWords = (totalDays: number) => {
  const years = Math.floor(totalDays / 365);
  const months = Math.floor((totalDays % 365) / 30);
  return years + ' yrs, ' + months + ' mos';
};
